import * as Sentry from "@sentry/react";
import { UserManager } from "oidc-client-ts";

export const registerAuthEventLogging = (userManager: UserManager) => {
  // A sign-in or a successful silent refresh stored a new user
  userManager.events.addUserLoaded((user) => {
    Sentry.logger.info(
      "User loaded event: Stored user replaced after sign-in or silent renew",
      {
        event_type: "user_loaded",
        expires_in: user.expires_in,
        expired: user.expired,
      },
    );
  });

  // Timer fired: the access token will expire within the next 3–5 minutes
  // This log runs before the automatic refresh starts
  userManager.events.addAccessTokenExpiring(() => {
    Sentry.logger.info("Access token expiring event", {
      event_type: "access_token_expiring",
      renewal_source: "timer",
    });
  });

  // Ping rejected the automatic refresh, the old user stays in localStorage
  // A timeout does not fire this. It retries after 5 seconds
  // A failure inside getUser() does not fire this either. That logs soft_logout
  userManager.events.addSilentRenewError((error) => {
    Sentry.logger.error("Silent renew error event", {
      event_type: "silent_renew_error",
      ...authErrorFields(error),
    });
  });

  // The access token lifetime has passed. This does not start a token refresh.
  userManager.events.addAccessTokenExpired(() => {
    Sentry.logger.warn(
      "Access token expired event: Access lifetime passed before a new user was stored",
      {
        event_type: "access_token_expired",
      },
    );
  });

  // This tab removed the stored user, including our own soft logout.
  // This is not a logout from Ping.
  userManager.events.addUserUnloaded(() => {
    Sentry.logger.info(
      "User unloaded event: oidc user removed from localStorage",
      {
        event_type: "user_unloaded",
      },
    );
  });
};

export const authErrorFields = (error: unknown) => {
  if (!error) return {};
  if (!(error instanceof Error)) return { error: String(error) };

  const fields: Record<string, string> = {
    error_name: error.name,
    error_message: error.message,
  };
  if ("error" in error && typeof error.error === "string") {
    fields.oidc_error = error.error;
  }
  if (
    "error_description" in error &&
    typeof error.error_description === "string"
  ) {
    fields.oidc_error_description = error.error_description;
  }
  return fields;
};

export const isResourceOurAPI = (resource: RequestInfo | URL) => {
  const resourceUrl =
    resource instanceof Request ? resource.url : resource.toString();
  return RegExp(`^(${window.location.origin})?/api/`).test(resourceUrl);
};

export const isCopilotAPI = (resource: RequestInfo | URL) => {
  const copilotApi = window.PULSE?.envPaths?.copilotApi;
  if (!copilotApi) return false;
  const resourceUrl =
    resource instanceof Request ? resource.url : resource.toString();
  return resourceUrl.startsWith(copilotApi);
};

export const setHeader = (
  resource: RequestInfo | URL,
  config: RequestInit,
  header: string,
  value: string,
) => {
  const resourceIsRequest = resource instanceof Request;

  if (resourceIsRequest) {
    resource.headers.set(header, value);
  }

  if (!resourceIsRequest || config.headers) {
    if (config.headers instanceof Headers) {
      config.headers.set(header, value);
    } else {
      config.headers = {
        ...config.headers,
        [header]: value,
      };
    }
  }
};
